import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Sparkles } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { applicationsApi } from "@/api/applications"
import { ApiError } from "@/api/client"
import { jobsApi } from "@/api/jobs"
import { type PipelineApplication, type PipelineStage, pipelineApi } from "@/api/pipeline"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/dashboard/page-header"
import { Skeleton } from "@/components/ui/skeleton"
import { initials } from "@/lib/utils"

export function PipelinePage() {
  const qc = useQueryClient()
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => jobsApi.list() })
  const jobList = jobs.data?.data ?? []
  const [jobId, setJobId] = React.useState<string>("")

  React.useEffect(() => {
    if (!jobId && jobList.length > 0) {
      const firstOpen = jobList.find((j) => j.status === "open") ?? jobList[0]
      setJobId(firstOpen.id)
    }
  }, [jobId, jobList])

  const stages = useQuery({
    queryKey: ["stages", jobId],
    queryFn: () => pipelineApi.stages(jobId || undefined),
    enabled: !!jobId,
  })
  const apps = useQuery({
    queryKey: ["pipeline-apps", jobId],
    queryFn: () => pipelineApi.applications(jobId || undefined),
    enabled: !!jobId,
  })

  const bulkAnalyze = useMutation({
    mutationFn: () => applicationsApi.analyzeBatch(jobId, 10),
    onSuccess: (res) => {
      toast.success(
        `${res.success}/${res.total} analiz tamamlandı${
          res.failed ? ` (${res.failed} hata)` : ""
        }.`,
      )
      qc.invalidateQueries({ queryKey: ["pipeline-apps"] })
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Toplu analiz başarısız."),
  })

  const move = useMutation({
    mutationFn: (vars: { applicationId: string; stageId: string }) =>
      pipelineApi.move(vars.applicationId, vars.stageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-apps"] })
      toast.success("Başvuru taşındı.")
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Taşınamadı."),
  })

  const stageList = stages.data?.data ?? []

  return (
    <>
      <PageHeader title="Pipeline" description="Adayları aşamalar arasında taşıyın." />
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-full space-y-1.5 sm:w-72">
            <Label>İlan</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger>
                <SelectValue placeholder="İlan seç…" />
              </SelectTrigger>
              <SelectContent>
                {jobList.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {jobId && (
            <Button
              variant="outline"
              onClick={() => bulkAnalyze.mutate()}
              disabled={bulkAnalyze.isPending}
            >
              <Sparkles className="mr-1.5 size-3.5" />
              {bulkAnalyze.isPending
                ? "Hirefy AI analiz ediyor…"
                : "Analizlenmemişleri analiz et"}
            </Button>
          )}
        </div>
      <div className="overflow-x-auto">
        <div className="flex gap-4">
          {!jobId && !jobs.isLoading && (
            <p className="text-muted-foreground">
              Pipeline'ı görmek için bir ilan seç.
            </p>
          )}
          {stages.isLoading && (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-96 w-72 shrink-0 rounded-xl" />
              ))}
            </>
          )}
          {jobId && stageList.length === 0 && !stages.isLoading && (
            <p className="text-muted-foreground">
              Bu ilana ait aşama yok.
            </p>
          )}
          {stageList.map((stage) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              stages={stageList}
              apps={(apps.data?.data ?? []).filter((a) => a.current_stage_id === stage.id)}
              onMove={(applicationId, stageId) => move.mutate({ applicationId, stageId })}
            />
          ))}
        </div>
      </div>
      </div>
    </>
  )
}

function StageColumn({
  stage,
  stages,
  apps,
  onMove,
}: {
  stage: PipelineStage
  stages: PipelineStage[]
  apps: PipelineApplication[]
  onMove: (applicationId: string, stageId: string) => void
}) {
  return (
    <div className="w-72 shrink-0">
      <div className="mb-2 flex items-center gap-2 px-1">
        <span
          className="size-2.5 rounded-full ring-2 ring-background"
          style={{ background: stage.color }}
        />
        <span className="text-sm font-semibold">{stage.name}</span>
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {apps.length}
        </span>
      </div>
      <div className="space-y-2 rounded-xl bg-muted/40 p-2">
        {apps.map((a) => (
          <Card
            key={a.id}
            className="border-transparent shadow-sm transition-all hover:border-primary/30 hover:shadow"
          >
            <CardContent className="space-y-2 p-3 text-sm">
              <div className="flex items-start gap-2">
                <div className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-medium text-primary">
                  {initials(a.candidate?.full_name)}
                </div>
                <div className="min-w-0 flex-1">
                  {a.candidate ? (
                    <Link
                      to={`/dashboard/candidates/${a.candidate.id}`}
                      className="block truncate font-medium hover:underline"
                    >
                      {a.candidate.full_name}
                    </Link>
                  ) : (
                    <div className="font-medium">—</div>
                  )}
                  <div className="truncate text-xs text-muted-foreground">
                    {a.job?.title ?? "—"}
                  </div>
                </div>
                {a.analysis?.match_score != null && (
                  <span className="shrink-0 rounded-md bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    {a.analysis.match_score}
                  </span>
                )}
              </div>
              <Select
                value={stage.id}
                onValueChange={(v) => {
                  if (v !== stage.id) onMove(a.id, v)
                }}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        ))}
        {apps.length === 0 && (
          <div className="rounded-lg border border-dashed bg-background p-4 text-center text-xs text-muted-foreground">
            Boş
          </div>
        )}
      </div>
    </div>
  )
}
